/**
 * @jest-environment node
*/

import { useEffect, useState } from 'react';

import { LocalStorage } from 'core/decorators';
import { denormalizeData } from 'core/utils/api';
import resolveRelationships from 'core/utils/relationships';

import { GAME_COMPARE_PATHNAME } from 'core/components/GameCompare/constants';

export function isGameCompare(pathname) {
  return pathname.indexOf(GAME_COMPARE_PATHNAME) === 0 || pathname.indexOf('/preview' + GAME_COMPARE_PATHNAME) === 0;
}

export const isCompareTopic = topicType => (topicType === 'game_compare') || (topicType === 'pair_compare');

/**
  * Получаем рандомные num карточки
*/
export const getRandom = (arr, num) => {
  const shuffled = [...arr].sort(() => 0.5 - Math.random());
  return shuffled.slice(0, num);
};

/**
  * Получаем объект, в котором ключи - раунды, значения - количество вариантов.
*/
export const getNumbersVariantsInRounds = (
  baseVariantsLength,
  result = {},
  round = 0,
  countRound = 0)  => {
  if (baseVariantsLength === 1 || baseVariantsLength === 0) {
    return result;
  }

  result[countRound] = (baseVariantsLength % 2 === 0) ? baseVariantsLength : baseVariantsLength - 1;

  let variantLength = baseVariantsLength;
  let currentRound = round;
  let countAllRound = countRound;

  do {
    if (variantLength >= 2) {
      variantLength = variantLength - 2;
      currentRound = currentRound + 1;
    } else {
      currentRound = currentRound + variantLength;
      variantLength = variantLength - 1;
    }
  } while (variantLength >= 1);

  countAllRound = countAllRound + 1;

  return getNumbersVariantsInRounds(currentRound, result, 0, countAllRound);
};

/**
  * Общее количество пар в игре
*/
export const getCountPairs = (allRounds) => {
  const countVarinants = Object.values(allRounds).reduce((sum, count) => sum + count, 0);
  return countVarinants / 2;
};

/**
  * Сумма процентов в предыдущих раундах.
*/
export const prevRoundsPercent = (allRounds, currentRound) => {
  if (!allRounds) return 0;

  let sumPercent = 0;
  for (let i = 0; i < currentRound; i++) {
    sumPercent += Math.floor(allRounds[i] / 2) * (100 / getCountPairs(allRounds));
  }
  return sumPercent;
};

/**
  * Получение данных c LS. Если LS пустой, то устанавливаются
  * дефолтные значения
*/
export const getGameCompareFromLS = (widgetId, type) => {
  const defaultGame = {
    variants: [],
    baseVariants: [],
    winners: [],
    currentRound: 0,
  };

  return JSON.parse(LocalStorage.getItem(`${type}-${widgetId}`)) || defaultGame;
};

/**
  * Запись данных в LS
*/
export const setGameCompareToLS = (widgetId, type, game) => {
  LocalStorage.setItem(`${type}-${widgetId}`, JSON.stringify(game));
};

/**
  * Формируем данные из реакций и вариантов
*/
export function getDataAggregation(reactions, variants) {
  const dataWithProgress = reactions.reduce((acc, reaction) => {
    const variant = variants.find(item => item.attributes.variant_id === reaction.id - 1);

    if (variant) {
      acc.push({
        ...variant,
        default: reaction?.default,
        final: reaction?.final,
      });
    }
    return acc;
  }, []);

  const res = variants.reduce((acc, variant) => {
    if (dataWithProgress.filter(data => data.id === variant.id)?.length === 0) {
      acc.push({
        ...variant,
        default: 0,
        final: 0,
      });
    }

    return acc;
  }, []);

  const result = [...dataWithProgress, ...res];
  return result;
}

/**
  * Количество пар в предыдущих раундах.
*/
export const prevRoundsPair = (allRounds, currentRound) => {
  let countPair = 0;
  for (let i = 0; i < currentRound; i++) {
    countPair += Math.floor(allRounds[i] / 2);
  }
  return countPair;
};


/**
  * Массив позиций для svg иконок на стр победителя.
*/
export const getPositions = (step, isMobile) => {
  const countWinners = isMobile ? 6 : 3;
  const positions = [0];
  let sum = 0;
  for (let i = 0; i < countWinners; i++) {
    sum += step;
    positions.push(sum);
  }

  return positions;
};


/**
  * Определяем победителя для парного сравнения
*/
export const getWinner = (votes, countPairs) => {
  let winner = '';

  if (votes.leftVotes > votes.rightVotes) {
    winner = 'left_winner';
  } else if (votes.rightVotes > votes.leftVotes) {
    winner = 'right_winner';
  } else if ((votes.rightVotes === votes.leftVotes) && (votes.rightVotes + votes.leftVotes === countPairs)) {
    winner = 'draw';
  }

  return winner;
};


/**
  * Возвращаем отсортированный рейтинг победителей
*/
export const getSortedRating = (reactions, widget) => {
  if (!reactions) return null;

  const finalReactions = reactions.pc_final;

  const {
    pc_final_draw: pcFinalDraw,
    pc_final_left: pcFinalLeft,
    pc_final_right: pcFinalRight,
  } = finalReactions;

  const {
    draw,
    left_winner: leftWinner,
    right_winner: rightWinner,
  } = widget.attributes;

  const mapKey = key => key.replace('right_winner', 'pc_final_right').replace('left_winner', 'pc_final_left').replace('draw', 'pc_final_draw');

  const winners = {
    draw: { ...draw, final: pcFinalDraw },
    left_winner: { ...leftWinner, final: pcFinalLeft },
    right_winner: { ...rightWinner, final: pcFinalRight },
  };

  const sortedWinners = Object.keys(winners)
    .map(key => ({ key, value: winners[key] }))
    .sort((a, b) => finalReactions[mapKey(b.key)] - finalReactions[mapKey(a.key)])
    .map(item => item.value);

  return sortedWinners;
};


/**
  * Возвращаем позицию победителя, который выбрал юзер
*/
export const getWinnerPosition = (ratings, winner) => ratings?.findIndex(variant => variant.id === winner?.id);


/**
  * Возвращаем пару соревнующихся
*/
export const getPair = (widgets, topicType) => {
  const compareWidget = widgets[0];
  let pair = [];
  if (topicType === 'pair_compare') {
    const {
      left_winner: leftWinner,
      right_winner: rightWinner,
    } = compareWidget.attributes;

    pair = [leftWinner, rightWinner];
  } else {
    pair = compareWidget?.attributes.variants.slice(0, 2);
  }
  return pair;
};

/**
  * Используем хук в карточках для топиков ИС и ПС.
*/

const relationships = resolveRelationships(['image', 'content'], {}, {
  image: {
    versions: {},
  },
  content: {},
});

export function useFetchAndUpdateWidgets(bebopApi, content) {
  const {
    id,
    attributes: {
      topic_type: topicType,
    },
  } = content;

  const {
    image: {
      versions: {
        original: originalCover,
      },
    },
  } = relationships(content) || {};

  const [widgets, updateWidgets] = useState(null);

  useEffect(() => {
    if (isCompareTopic(topicType) && !originalCover) {
      bebopApi
        .getTopics({
          include: 'content',
          fields: '',
          ids: id,
        })
        .then(denormalizeData)
        .then(data => {
          const {
            content: {
              widgets: widgetsFromData,
            },
          } = relationships(data[0]);

          if (widgetsFromData?.length > 0) {
            updateWidgets(widgetsFromData);
          }
        });
    }
  }, [bebopApi, id, topicType, originalCover]);

  return widgets;
}
